import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import moment from 'moment'
import DualDateRangePicker from 'components/Common/Calendar/DualDateRangePicker'

import SearchDateFooter from './SearchDateFooter'

import { addDays, addMonths, beginningOfToday } from 'lib/dateUtils'
import SearchDateToggle from './SearchDateToggle'
import {
  GlobalSearchDispatchContext,
  GlobalSearchStateContext,
} from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS, FLEXIBLE_DURATION_RANGE } from 'constants/search'
import FlexibleDatePicker from './FlexibleDatePicker'
import config from 'constants/config'
import { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'

const Wrapper = styled.div`
  margin: 0 auto;
`

const DatesToggle = styled(SearchDateToggle)`
  margin: ${rem(32)} auto 0;
  width: fit-content;
`

const SearchDatePicker = styled(DualDateRangePicker)`
  display: flex;
  background-color: ${(props) => props.theme.palette.neutral.eight};
  margin-left: auto;
  margin-right: auto;
  flex-grow: 1;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${rem(48)};
`

const Dropdown = styled.div`
  box-shadow: ${props => props.theme.shadow.flat.small};
  position: absolute;
  top: calc(100% + ${rem(8)});
  left: 0;
  width: 100%;
  background: ${({ theme }) => theme.palette.neutral.eight};
  z-index: ${zIndex.menu};
  border-radius: ${props => props.theme.borderRadius.medium};
`

interface Props {
  onDatesApply: () => void;
  onDatesDecline: () => void;
  isLastStep?: boolean;
  anytimeDateSelected?: boolean;
  selectAnytimeDate?: (shouldApply: boolean) => void;
  selectSpecificDates?: () => void;
  selectFlexibleDates?: () => void;
  selectAnytime?: () => void;
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS;
  durationOptionsCustom?: DurationOption[];
  isSpecificSearchEnabled?: boolean
}

const today = beginningOfToday()

function SearchDateDropDown({
  onDatesApply,
  onDatesDecline,
  isLastStep,
  anytimeDateSelected,
  selectAnytimeDate,
  selectSpecificDates,
  selectFlexibleDates,
  dateSearchOptionId,
  durationOptionsCustom,
  isSpecificSearchEnabled = true,
}: Props) {
  const { checkinDate, checkoutDate, flexibleMonths, flexibleNights, userSelectedFlexibleMonths } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const maxDate = useMemo(
    () =>
      checkinDate && !checkoutDate ?
        addDays(checkinDate.toDate(), 31) :
        addMonths(today, 36),
    [checkinDate, checkoutDate],
  )

  const handleDatesChanged = useCallback(
    (dates: { startDate: moment.Moment; endDate: moment.Moment }) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKIN_DATE,
        date: dates.startDate,
      })
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
        date: dates.endDate,
      })

      if (dates.startDate || dates.endDate) {
        selectSpecificDates?.()
      }
    },
    [globalSearchDispatch, selectSpecificDates],
  )

  const handleFlexibleNightsChanged = useCallback(
    (flexibleNights: FLEXIBLE_DURATION_RANGE) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights,
      })
    },
    [globalSearchDispatch],
  )

  const handleFlexibleMonthOptionsChanged = useCallback(
    (flexibleMonthOptions: string) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
        flexibleMonths: flexibleMonthOptions,
      })
      if (flexibleMonthOptions.length > 0) {
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
      } else {
        globalSearchDispatch({
          type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS,
        })
      }
    },
    [globalSearchDispatch],
  )

  const selectAnytimeDateNoApply = useCallback(() => {
    selectAnytimeDate(false)
  }, [selectAnytimeDate])

  return (
    <Dropdown>
      <Wrapper>
        {config.businessTraveller.currentAccountMode !== 'business' && <DatesToggle
          onSelectAnytime={selectAnytimeDateNoApply}
          onSelectSpecific={selectSpecificDates}
          anytimeDateSelected={anytimeDateSelected}
          onSelectFlexible={selectFlexibleDates}
          dateSearchOptionId={dateSearchOptionId}
          isSpecificSearchEnabled={isSpecificSearchEnabled}
        />}
        {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
          <FlexibleDatePicker
            onMonthChange={handleFlexibleMonthOptionsChanged}
            onDurationChange={handleFlexibleNightsChanged}
            previouslySelectedDuration={flexibleNights}
            previouslySelectedMonths={userSelectedFlexibleMonths ? flexibleMonths : undefined}
            durationOptionsCustom={durationOptionsCustom}
          />
        )}
        {isSpecificSearchEnabled && dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
          <SearchDatePicker
            onDatesChange={handleDatesChanged}
            startDate={checkinDate}
            endDate={checkoutDate}
            minDate={today}
            maxDate={maxDate}
            startLabel="Check in"
            endLabel="Check out"
          />
        )}
        <SearchDateFooter
          onApply={onDatesApply}
          onDecline={onDatesDecline}
          isLastStep={isLastStep}
          flexibleDateSelected={anytimeDateSelected}
        />
      </Wrapper>
    </Dropdown>
  )
}

export default SearchDateDropDown
